import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
    ArrowLeft, Check, X, AlertTriangle, TrendingUp, Weight, Syringe, Stethoscope,
    Truck, DollarSign, ShieldCheck, Users, Calendar, Activity, Baby, Plus, Printer, QrCode, Skull, Droplets, MapPin, NotebookPen,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import AddEventDialog from "@/components/AddEventDialog";
import { useAuth } from "@/context/AuthContext";
import { farmDateIso } from "@/lib/farmTime";

const STAGE_COLORS = {
    newborn: "bg-pink-500/15 text-pink-700 border-pink-500/40",
    suckling: "bg-pink-500/15 text-pink-700 border-pink-500/40",
    brooding: "bg-amber-500/15 text-amber-700 border-amber-500/40",
    weaned: "bg-blue-500/15 text-blue-700 border-blue-500/40",
    growing: "bg-blue-500/15 text-blue-700 border-blue-500/40",
    finishing: "bg-primary/15 text-primary border-primary/40",
    ready_for_sale: "bg-primary/15 text-primary border-primary/40",
    reserved: "bg-purple-500/15 text-purple-700 border-purple-500/40",
    sold: "bg-secondary text-foreground border-border",
    deceased: "bg-destructive/15 text-destructive border-destructive/40",
    culled: "bg-destructive/15 text-destructive border-destructive/40",
    unknown: "bg-secondary text-foreground border-border",
};

function buildTimelineFromProfile(profile) {
    if (!profile) return [];
    const items = [];

    const getImages = (row) => {
        const out = [];
        ["photo_url", "image_url", "file_url", "photo_data_url", "receipt_url"].forEach((key) => {
            if (row?.[key]) out.push(row[key]);
        });
        return Array.from(new Set(out));
    };

    const getAttachments = (row) => {
        const out = [];
        if (row?.photo_file_id || row?.photo_url) {
            out.push({
                file_id: row?.photo_file_id,
                name: row?.photo_filename || "photo",
                kind: "photo",
                url: row?.photo_url || row?.image_url || row?.file_url,
            });
        }
        if (row?.file_id || row?.file_url) {
            out.push({
                file_id: row?.file_id,
                name: row?.original_filename || row?.name || "attachment",
                kind: "file",
                url: row?.file_url,
            });
        }
        return out;
    };

    const add = (eventType, rows, makeDetails) => {
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const eventAt = row?.created_at || row?.timestamp || row?.date || "";
            const eventDate = String(eventAt).includes("T") ? String(eventAt).split("T", 1)[0] : String(eventAt || "");
            const eventTime = String(eventAt).includes("T") ? String(eventAt).split("T", 1)[1]?.replace("Z", "") || "" : "";
            const images = getImages(row);
            const attachments = getAttachments(row);
            items.push({
                eventType,
                eventAt: String(eventAt || ""),
                eventDate,
                eventTime,
                actor: row?.employee_name || row?.recorded_by || row?.created_by || null,
                details: makeDetails(row),
                notes: row?.notes || row?.vet_notes || "",
                images,
                attachments,
                recordId: row?.id,
                hasPhoto: images.length > 0 || attachments.length > 0,
            });
        });
    };

    add("weight", profile.weights, (row) => `Weight ${Number(row?.weight ?? row?.weight_kg ?? row?.value ?? 0).toFixed(1)} kg`);
    add("feeding", profile.feedings, (row) => `Feeding ${row?.feed_type || "feed"}`);
    add("water_check", profile.water_checks, (row) => `Water ${row?.water_quality || "checked"} · ${row?.location || "location not recorded"}`);
    add("vaccination", profile.vaccinations, (row) => row?.vaccine || row?.vaccine_name || "Vaccination");
    (Array.isArray(profile.treatments) ? profile.treatments : []).forEach((row) => {
        const type = String(row?.treatment_type || "").toLowerCase();
        const eventType = type === "deworming" ? "deworming" : type === "vet_visit" ? "vet_visit" : "medication";
        add(eventType, [row], () => {
            if (eventType === "deworming") return row?.medicine || row?.medicine_name || "Deworming";
            if (eventType === "vet_visit") return row?.visit_type || row?.veterinarian || "Vet visit";
            return row?.medicine || row?.medicine_name || "Medication";
        });
    });
    add("health_inspection", profile.health, (row) => row?.condition || row?.issue || "Health inspection");
    add("breeding", profile.breedings, (row) => `Breeding ${row?.method || "recorded"}`);
    add("gestation", profile.gestations, (row) => `Gestation ${row?.pregnancy_stage || row?.gestation_status || "active"}`);
    add("pregnancy_check", profile.pregnancy_checks, (row) => `Pregnancy ${row?.result || "recorded"}`);
    add("farrowing", profile.farrowings, (row) => `Farrowing ${row?.live_born ?? 0} live`);
    add("medical_schedule", profile.medical_schedules, (row) => `Schedule ${row?.schedule_name || row?.schedule_type || "medical"} · ${row?.status || "scheduled"}`);
    add("movement", profile.movements, (row) => `Moved ${row?.from_location || "—"} → ${row?.to_location || "—"}`);
    add("mortality", profile.mortality ? [profile.mortality] : [], (row) => `Mortality · ${row?.cause || "recorded"}`);

    return items.sort((a, b) => (a.eventAt || "").localeCompare(b.eventAt || ""));
}

export default function AnimalProfilePage() {
    const { aid } = useParams();
    const navigate = useNavigate();
    const { isOwner } = useAuth();
    const [p, setP] = useState(null);
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [busy, setBusy] = useState(false);
    const [addKind, setAddKind] = useState(null);
    const [showQR, setShowQR] = useState(false);
    const [timeline, setTimeline] = useState([]);
    const [activeTab, setActiveTab] = useState("overview");

    const load = useCallback(async () => {
        try {
            const [prof, f] = await Promise.all([
                api.get(`/animals/${aid}/profile`).then((r) => r.data),
                api.get("/farm").then((r) => r.data).catch(() => ({ currency: "PHP" })),
            ]);
            setP(prof); setFarm(f || { currency: "PHP" });
            setTimeline(buildTimelineFromProfile(prof));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load profile");
            navigate("/animals");
        }
    }, [aid, navigate]);
    useEffect(() => { load(); }, [load]);

    const approveForSale = async () => {
        setBusy(true);
        try {
            await api.post(`/animals/${aid}/approve-for-sale`);
            toast.success("Approved for sale");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    // When mortality is saved, also flip the animal's status to 'deceased'.
    const onEventSaved = async (kind) => {
        if (kind === "mortality") {
            try {
                await api.post(`/animals/${aid}/set-life-stage`, { stage: "deceased", reason: "Mortality recorded" });
            } catch (err) { /* non-fatal */ }
        }
        load();
    };

    if (!p) return <div className="p-6 text-muted-foreground" data-testid="animal-profile-loading">{i18n.t("batch2.33ce417454bf")}</div>;

    const a = p.animal;
    const cur = farm.currency || "PHP";
    const stageColor = STAGE_COLORS[p.life_stage] || STAGE_COLORS.unknown;
    const speciesName = String(p.species?.name || "").toLowerCase();
    const isAquatic = ["tilapia", "catfish", "hito", "fish", "milkfish", "shrimp"].some((name) => speciesName.includes(name));
    const isAvian = ["chicken", "duck", "turkey", "quail", "goose", "bird"].some((name) => speciesName.includes(name));
    const supportsMammalReproduction = !isAquatic && !isAvian;
    const resolveAnimalWeight = (animal) => {
        const candidates = [
            animal?.custom_fields?.weight_kg,
            animal?.weight_kg,
            animal?.current_weight,
            animal?.weight,
        ];

        for (const value of candidates) {
            if (value === null || value === undefined || value === "") continue;
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }

        return null;
    };

    const animalLatestWeight = resolveAnimalWeight(a);
    const latestProfileWeight = p.metrics?.latest_weight != null ? Number(p.metrics.latest_weight) : null;
    const resolvedLatestWeight = latestProfileWeight != null ? latestProfileWeight : animalLatestWeight;
    const weightHistoryRows = Array.isArray(p.weights)
        ? p.weights
            .map((entry) => ({
                date: entry.date,
                created_at: entry.created_at,
                weight: Number(entry.weight ?? entry.weight_kg ?? entry.value ?? 0),
                feed_kg: Number(entry.feed_kg ?? 0),
            }))
            .filter((entry) => Number.isFinite(entry.weight) && entry.weight > 0)
            .sort((a, b) => `${a.date || ""}T${a.created_at || ""}`.localeCompare(`${b.date || ""}T${b.created_at || ""}`))
        : [];
    const previousWeight = weightHistoryRows.length >= 2 ? weightHistoryRows[weightHistoryRows.length - 2].weight : null;
    const totalGain = resolvedLatestWeight != null && previousWeight != null ? resolvedLatestWeight - previousWeight : null;
    const gainSincePrevious = previousWeight != null && resolvedLatestWeight != null ? resolvedLatestWeight - previousWeight : null;
    const latestWeightValue = resolvedLatestWeight != null ? resolvedLatestWeight : (p.metrics?.latest_weight != null ? Number(p.metrics.latest_weight) : null);
    const latestWeightDisplay = latestWeightValue != null ? `${latestWeightValue.toFixed(1)} kg` : "—";
    const previousWeightDisplay = previousWeight != null ? `${previousWeight.toFixed(1)} kg` : "—";
    const totalGainDisplay = totalGain != null ? `${totalGain.toFixed(1)} kg` : "—";
    const gainSincePreviousDisplay = gainSincePrevious != null ? `${gainSincePrevious.toFixed(1)} kg` : "—";
    const adgValue = p.metrics?.avg_daily_gain != null ? Number(p.metrics.avg_daily_gain) : null;
    const adgDisplay = adgValue != null ? `${adgValue.toFixed(2)} kg/d` : "—";
    const latestFeed = weightHistoryRows.length > 0 ? Number(weightHistoryRows[weightHistoryRows.length - 1].feed_kg ?? 0) : null;
    const previousFeed = weightHistoryRows.length > 1 ? Number(weightHistoryRows[weightHistoryRows.length - 2].feed_kg ?? 0) : null;
    const feedSincePrevious = previousFeed != null && latestFeed != null ? latestFeed - previousFeed : null;
    const feedSincePreviousDisplay = feedSincePrevious != null ? `${feedSincePrevious.toFixed(1)} kg` : "—";
    const fcrValue = gainSincePrevious != null && gainSincePrevious > 0 && feedSincePrevious != null && feedSincePrevious > 0 ? feedSincePrevious / gainSincePrevious : null;
    const fcrDisplay = fcrValue != null ? fcrValue.toFixed(2) : "—";

    return (
        <div className="space-y-6 pb-8" data-testid="animal-profile-page">
            <div className="flex flex-wrap items-start gap-3 print:hidden">
                <Button variant="ghost" size="sm" onClick={() => navigate("/animals")} data-testid="animal-profile-back">
                    <ArrowLeft className="w-4 h-4 mr-1" />{i18n.t("batch2.260ff50a7c28")}
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="tiny-label">
                        {p.species?.name || "Animal"}{a.breed ? ` · ${a.breed}` : ""}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-3xl font-bold" data-testid="animal-profile-tag">{a.tag_id || a.name || "Untitled"}</h1>
                        <Badge variant="outline" className={`${stageColor} capitalize`} data-testid="animal-profile-stage">{p.life_stage.replace(/_/g, " ")}</Badge>
                        {p.age_days != null && <Badge variant="outline" data-testid="animal-profile-age">{p.age_days}{i18n.t("batch2.9e883160f12a")}</Badge>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowQR(true)} data-testid="animal-profile-qr-btn"><QrCode className="w-4 h-4 mr-1" />{i18n.t("batch2.45e7a1c379c7")}</Button>
                    <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="animal-profile-print-btn"><Printer className="w-4 h-4 mr-1" />{i18n.t("batch2.5b221e9c2a45")}</Button>
                </div>
                {isOwner() && p.readiness ? <ReadinessBadge readiness={p.readiness} onApprove={approveForSale} busy={busy} /> : null}
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <SummaryStat icon={Weight} label={i18n.t("batch2.295c43d41882")} value={resolvedLatestWeight != null ? `${resolvedLatestWeight.toFixed(1)} kg` : "—"} testid="summary-latest-weight" />
                <SummaryStat icon={TrendingUp} label={i18n.t("batch2.5e5837db8186")} value={`${p.metrics.total_gain} kg`} sub={`ADG ${p.metrics.avg_daily_gain} kg/d`} testid="summary-total-gain" />
                <SummaryStat icon={DollarSign} label={i18n.t("batch2.b5d8da40f3ed")} value={formatCurrency(p.expenses.total, cur)} testid="summary-total-cost" />
                <SummaryStat icon={DollarSign} label={i18n.t("batch2.07e6e386d87a")} value={formatCurrency(p.metrics.estimated_value, cur)} testid="summary-est-value" />
                <SummaryStat icon={ShieldCheck} label={p.metrics.expected_profit >= 0 ? "Expected Profit" : "Expected Loss"}
                    value={formatCurrency(Math.abs(p.metrics.expected_profit), cur)}
                    tone={p.metrics.expected_profit >= 0 ? "green" : "red"} testid="summary-expected-profit" />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="flex flex-wrap h-auto" data-testid="animal-profile-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview"><Activity className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.0efc2e6be4c2")}</TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="tab-timeline"><Calendar className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.018514a3d58a")}</TabsTrigger>
                    <TabsTrigger value="weight" data-testid="tab-weight"><Weight className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.69c0b81540bd")}</TabsTrigger>
                    <TabsTrigger value="health" data-testid="tab-health"><Stethoscope className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.3703cd216881")}</TabsTrigger>
                    <TabsTrigger value="breeding" data-testid="tab-breeding"><Baby className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.f93f25f348f3")}</TabsTrigger>
                    <TabsTrigger value="gestation" data-testid="tab-gestation"><Baby className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.5cbf60902ee2")}</TabsTrigger>
                    <TabsTrigger value="feeding" data-testid="tab-feeding"><Truck className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.4ec763aab4f8")}</TabsTrigger>
                    <TabsTrigger value="care" data-testid="tab-daily-care"><Droplets className="w-3.5 h-3.5 mr-1" />Daily Care</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents"><Users className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.687c82861c95")}</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <OverviewTab p={p} />
                </TabsContent>
                <TabsContent value="timeline">
                    <TimelineTab items={timeline} />
                </TabsContent>
                <TabsContent value="weight">
                    <TabHeader title={i18n.t("batch2.69c0b81540bd")} onAdd={() => setAddKind("weight")} testid="add-weight-btn" />
                    <div className="space-y-4">
                        <Card className="p-4" data-testid="animal-weight-summary">
                            <h3 className="font-semibold mb-3">{i18n.t("batch2.325760e748ec")}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.295c43d41882")}</div>
                                    <div className="font-semibold mt-1">{latestWeightDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.395703d5f5b9")}</div>
                                    <div className="font-semibold mt-1">{previousWeightDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.5e5837db8186")}</div>
                                    <div className="font-semibold mt-1">{totalGainDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.5d6b50bb295d")}</div>
                                    <div className="font-semibold mt-1">{gainSincePreviousDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.6527e706017d")}</div>
                                    <div className="font-semibold mt-1">{adgDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.195728a9d22b")}</div>
                                    <div className="font-semibold mt-1">{feedSincePreviousDisplay}</div>
                                </div>
                                <div className="rounded-lg border bg-muted/20 p-3">
                                    <div className="tiny-label">{i18n.t("batch2.1f85f16afefa")}</div>
                                    <div className="font-semibold mt-1">{fcrDisplay}</div>
                                </div>
                            </div>
                        </Card>
                        {activeTab === "weight" ? <WeightTab p={p} latestWeightValue={resolvedLatestWeight} /> : null}
                    </div>
                </TabsContent>
                <TabsContent value="health">
                    <TabHeader title={i18n.t("batch2.201c869f726a")} onAdd={() => setAddKind("health")} testid="add-health-btn" />
                    <HealthTab p={p} cur={cur} onAdd={setAddKind} isOwner={Boolean(isOwner())} />
                </TabsContent>
                <TabsContent value="breeding">
                    <BreedingTab p={p} onAdd={setAddKind} supportsMammalReproduction={supportsMammalReproduction} />
                </TabsContent>
                <TabsContent value="gestation">
                    <GestationTab p={p} onAdd={setAddKind} supportsMammalReproduction={supportsMammalReproduction} />
                </TabsContent>
                <TabsContent value="feeding">
                    <FeedingTab p={p} onAdd={setAddKind} />
                </TabsContent>
                <TabsContent value="care">
                    <DailyCareTab p={p} onAdd={setAddKind} />
                </TabsContent>
                <TabsContent value="documents">
                    <DocumentsTab p={p} aid={aid} onReload={load} />
                </TabsContent>
            </Tabs>

            {addKind && (
                <AddEventDialog
                    kind={addKind}
                    animalId={aid}
                    onSaved={() => onEventSaved(addKind)}
                    onClose={() => setAddKind(null)}
                />
            )}

            {showQR && <QRDialog animal={a} species={p.species} onClose={() => setShowQR(false)} />}
        </div>
    );
}

function TabHeader({ title, onAdd, testid }) {
    return (
        <div className="flex items-center justify-between mb-3 print:hidden">
            <h3 className="font-semibold text-lg">{title}</h3>
            <Button size="sm" onClick={onAdd} data-testid={testid}>
                <Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.61cc55aa0453")}
            </Button>
        </div>
    );
}

function QRDialog({ animal, species, onClose }) {
    const printRef = useRef(null);
    const payload = JSON.stringify({ id: animal.id, tag: animal.tag_id, species: species?.name || "" });
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-dialog">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
                <div ref={printRef}>
                    <div className="font-bold text-lg">{animal.tag_id || animal.name}</div>
                    <div className="text-xs text-muted-foreground mb-3">{species?.name || ""}{animal.breed ? ` · ${animal.breed}` : ""}</div>
                    <div className="flex justify-center mb-3">
                        <QRCodeSVG value={payload} size={220} includeMargin data-testid="qr-code-svg" />
                    </div>
                    <div className="text-[10px] text-muted-foreground break-all">{animal.id}</div>
                </div>
                <div className="flex gap-2 mt-4 print:hidden">
                    <Button variant="outline" onClick={onClose} className="flex-1">{i18n.t("batch2.bbfa773e5a63")}</Button>
                    <Button onClick={() => window.print()} className="flex-1" data-testid="qr-print-btn"><Printer className="w-4 h-4 mr-1" />{i18n.t("batch2.5b221e9c2a45")}</Button>
                </div>
            </div>
        </div>
    );
}

// ---------- Building blocks ----------
function SummaryStat({ icon: Icon, label, value, sub, tone, testid }) {
    const toneCls = tone === "green" ? "text-primary" : tone === "red" ? "text-destructive" : "";
    return (
        <Card className="p-3" data-testid={testid}>
            <div className="tiny-label flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{label}</div>
            <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </Card>
    );
}

function ReadinessBadge({ readiness, onApprove, busy }) {
    const cls = readiness.state === "ready"
        ? "bg-primary/15 text-primary border-primary/40"
        : readiness.state === "almost_ready"
            ? "bg-amber-500/15 text-amber-700 border-amber-500/40"
            : "bg-destructive/15 text-destructive border-destructive/40";
    return (
        <div className="min-w-[220px]">
            <Card className={`p-3 border ${cls}`} data-testid="animal-profile-readiness">
                <div className="text-xs uppercase tracking-wider opacity-80">{i18n.t("batch2.725bc458ce08")}</div>
                <div className="text-lg font-bold" data-testid="animal-profile-readiness-summary">{readiness.summary}</div>
                <div className="text-xs opacity-80 mb-2">{readiness.passed}/{readiness.total} {i18n.t("batch2.9cc46760be1f")}</div>
                <Progress value={(readiness.passed / readiness.total) * 100} className="h-2 mb-2" />
                {readiness.state !== "ready" && (
                    <Button size="sm" variant="outline" onClick={onApprove} disabled={busy} className="w-full" data-testid="animal-profile-approve-btn">
                        {i18n.t("batch2.304918a345d2")}
                    </Button>
                )}
            </Card>
        </div>
    );
}

function OverviewTab({ p }) {
    const a = p.animal;
    const profile = a.profile || p.profile || {};
    const ageLabel = p.age_days != null ? `${p.age_days} days` : "—";
    const latestWeight = p.metrics?.latest_weight;
    const photoUrl = profile.photoUrl || a.photo_url || a.photoUrl;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 space-y-2 text-sm">
                {photoUrl ? <img src={photoUrl} alt={i18n.t("batch2.7389b3b752fa")} className="w-full h-48 object-cover rounded-xl border" /> : <div className="h-48 rounded-xl border grid place-items-center text-muted-foreground">{i18n.t("batch2.b7caebb3a0d2")}</div>}
            </Card>
            <Card className="p-4 space-y-2 text-sm">
                <h3 className="font-semibold mb-2">{i18n.t("batch2.7e5a975b6add")}</h3>
                <Row k="Animal Tag" v={profile.animalTag || a.tag_id} />
                <Row k="Species / Breed" v={`${profile.speciesName || p.species?.name || "—"}${profile.breed ? ` · ${profile.breed}` : ""}`} />
                <Row k="Sex / Age" v={`${profile.sex || "—"} · ${ageLabel}`} />
                <Row k="Current Weight" v={latestWeight != null ? `${Number(latestWeight).toFixed(1)} kg` : (profile.currentWeight != null ? `${Number(profile.currentWeight).toFixed(1)} kg` : "—")} />
                <Row k="Pen / Area" v={`${profile.pen || "—"}${profile.assignedArea ? ` · ${profile.assignedArea}` : ""}`} />
                <Row k="Assigned Employee" v={`${profile.assignedEmployeeName || "—"}${profile.assignedEmployeeId ? ` · ${profile.assignedEmployeeId}` : ""}`} />
                <Row k="Status" v={profile.status || a.status} />
                <Row k="Sire / Dam" v={`${profile.sireId || "—"} / ${profile.damId || "—"}`} />
                <Row k="Acquisition" v={`${profile.acquisitionType || "—"}${profile.acquisitionDate ? ` · ${profile.acquisitionDate}` : ""}`} />
                <Row k="RFID / Ear Tag" v={`${profile.rfid || "—"}${profile.earTag ? ` · ${profile.earTag}` : ""}`} />
                <Row k="Notes" v={profile.notes || "—"} />
            </Card>
            <Card className="p-4">
                <h3 className="font-semibold mb-2">{i18n.t("batch2.25b2f1c9237e")}</h3>
                <ul className="space-y-1.5" data-testid="animal-readiness-list">
                    {p.readiness.checks.map((c) => (
                        <li key={c.key} className="flex items-start gap-2 text-sm">
                            {c.ok ? <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /> : <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />}
                            <div className="min-w-0">
                                <div className={c.ok ? "text-foreground" : "text-destructive font-medium"}>{c.label}</div>
                                <div className="text-xs text-muted-foreground truncate">{c.detail}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </Card>
        </div>
    );
}

function HealthTab({ p, cur, onAdd, isOwner }) {
    const inspections = Array.isArray(p.health) ? p.health : [];
    const treatments = Array.isArray(p.treatments) ? p.treatments : [];
    const vaccinations = Array.isArray(p.vaccinations) ? p.vaccinations : [];
    const [filterType, setFilterType] = useState("all");
    const [selected, setSelected] = useState(null);
    const medicalAlerts = Array.isArray(p.medical_alerts) ? p.medical_alerts : [];
    const scheduleUpcoming = Array.isArray(p.medical_schedule_upcoming) ? p.medical_schedule_upcoming : [];
    const scheduleCompleted = Array.isArray(p.medical_schedule_completed) ? p.medical_schedule_completed : [];
    const scheduleOverdue = Array.isArray(p.medical_schedule_overdue) ? p.medical_schedule_overdue : [];

    const resolveCost = (row) => {
        if (!row || typeof row !== "object") return null;
        const keys = ["cost", "amount", "total_cost", "medication_cost", "expense_cost"];
        for (const key of keys) {
            const value = row[key];
            if (value === null || value === undefined || value === "") continue;
            const num = Number(value);
            if (!Number.isNaN(num)) return num;
        }
        return null;
    };

    const unifiedRows = useMemo(() => {
        const rows = [];

        inspections.forEach((row) => {
            rows.push({
                type: "health",
                date: row.date || row.created_at || "",
                title: row.issue || row.condition || row.description || "Health check",
                status: row.status || row.recovery_status || "",
                actor: row.employee_name || row.recorded_by || row.created_by || "",
                notes: row.notes || row.vet_notes || "",
                next_due_date: row.next_due_date || row.follow_up_date || "",
                hasMedia: Boolean(row.photo_url || row.photo_file_id || row.photo_data_url),
                row,
            });
        });

        treatments.forEach((row) => {
            const tt = String(row.treatment_type || "medication").toLowerCase();
            rows.push({
                type: tt === "deworming" ? "deworming" : tt === "vet_visit" ? "vet_visit" : "medication",
                date: row.date || row.created_at || "",
                title: row.medicine || row.medicine_name || row.visit_type || "Treatment",
                status: row.status || row.recovery_status || "",
                actor: row.administered_by || row.employee_name || row.recorded_by || row.created_by || "",
                notes: row.notes || row.vet_notes || row.issue || "",
                next_due_date: row.follow_up_date || row.withdrawal_end_date || "",
                cost: resolveCost(row),
                hasMedia: Boolean(row.photo_url || row.photo_file_id || row.photo_data_url),
                row,
            });
        });

        vaccinations.forEach((row) => {
            rows.push({
                type: "vaccination",
                date: row.date_given || row.date_administered || row.scheduled_date || row.created_at || "",
                title: row.vaccine || row.vaccine_name || "Vaccination",
                status: row.status || "",
                actor: row.administrator || row.employee_name || row.recorded_by || row.created_by || "",
                notes: row.notes || row.vet_notes || "",
                next_due_date: row.next_due_date || "",
                cost: resolveCost(row),
                hasMedia: Boolean(row.photo_url || row.photo_file_id || row.photo_data_url),
                row,
            });
        });

        return rows.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    }, [inspections, treatments, vaccinations]);

    const filteredRows = useMemo(() => {
        if (filterType === "all") return unifiedRows;
        return unifiedRows.filter((row) => row.type === filterType);
    }, [unifiedRows, filterType]);

    const today = farmDateIso();
    const dueSoonCount = medicalAlerts.length > 0
        ? medicalAlerts.length
        : unifiedRows.filter((row) => {
            if (!row.next_due_date) return false;
            return row.next_due_date <= today;
        }).length;

    const activeCases = unifiedRows.filter((row) => ["under_treatment", "under_observation", "critical"].includes(String(row.status || "").toLowerCase())).length;

    const addButtonCls = "h-8 px-3";
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 print:hidden">
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("health")}>{i18n.t("batch2.f9096ddd8468")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("treatment")}>{i18n.t("batch2.d0940f026be6")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("vaccination")}>{i18n.t("batch2.c0f789c54889")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("vaccination_schedule")}>{i18n.t("batch2.e57310bd5fa4")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("treatment_schedule")}>{i18n.t("batch2.ea8cd0b9a0bd")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("deworming")}>{i18n.t("batch2.80029aac6ba4")}</Button>
                <Button size="sm" variant="outline" className={addButtonCls} onClick={() => onAdd && onAdd("vet_visit")}>{i18n.t("batch2.ef3b54d66fd8")}</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="animal-health-summary-cards">
                <Card className="p-3"><div className="tiny-label">{i18n.t("batch2.30c3a17c5a54")}</div><div className="font-semibold mt-1">{unifiedRows.length}</div></Card>
                <Card className="p-3"><div className="tiny-label">{i18n.t("batch2.bc2bbcfd991a")}</div><div className="font-semibold mt-1">{activeCases}</div></Card>
                <Card className="p-3"><div className="tiny-label">{i18n.t("batch2.e9da79148e64")}</div><div className="font-semibold mt-1">{dueSoonCount}</div></Card>
                <Card className="p-3"><div className="tiny-label">{i18n.t("batch2.43aa47469f02")}</div><div className="font-semibold mt-1">{vaccinations.filter((v) => String(v.status || "").toLowerCase() === "completed").length}</div></Card>
            </div>

            {medicalAlerts.length > 0 ? (
                <Card className="p-3" data-testid="animal-health-alerts">
                    <h3 className="font-semibold mb-2">{i18n.t("batch2.6c7dc36d0d76")}</h3>
                    <div className="space-y-1.5 text-sm">
                        {medicalAlerts.slice(0, 8).map((a, idx) => (
                            <div key={`${a.kind || "alert"}-${a.record_id || idx}`} className="flex items-center justify-between gap-2 rounded border p-2">
                                <div>
                                    <div className="font-medium">{a.title || "Medical alert"}</div>
                                    <div className="text-xs text-muted-foreground">{a.date || ""}</div>
                                </div>
                                <StatusPill v={a.severity || "warning"} />
                            </div>
                        ))}
                    </div>
                </Card>
            ) : null}

            <Card className="p-3" data-testid="animal-health-unified-table">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="font-semibold">{i18n.t("batch2.5b83c8c1f5d4")}</h3>
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
                        <option value="all">{i18n.t("batch2.30c8a0fc9c1a")}</option>
                        <option value="health">{i18n.t("batch2.87398e24ea58")}</option>
                        <option value="medication">{i18n.t("batch2.36da837e8b37")}</option>
                        <option value="vaccination">{i18n.t("batch2.22dbc070acdf")}</option>
                        <option value="deworming">{i18n.t("batch2.df9c88127462")}</option>
                        <option value="vet_visit">{i18n.t("batch2.6451a93db098")}</option>
                    </select>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border rounded-lg">
                        <thead className="bg-muted/40">
                            <tr>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.eb9a4bc1c0c1")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.3deb74565196")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.1c54132b8e5a")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.bae7d5be7082")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.59bba0608a3c")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.cfadbd7a5771")}</th>
                                {isOwner ? <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.64ae43e8fe76")}</th> : null}
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.97c89a4d6630")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRows.length === 0 ? (
                                <tr>
                                    <td className="p-3 text-muted-foreground" colSpan={isOwner ? 8 : 7}>{i18n.t("batch2.1675280ca24d")}</td>
                                </tr>
                            ) : (
                                filteredRows.map((item, idx) => (
                                    <tr key={`${item.type}-${item.row?.id || idx}`} className="border-t">
                                        <td className="p-2">{item.date || "—"}</td>
                                        <td className="p-2 capitalize">{item.type.replace(/_/g, " ")}</td>
                                        <td className="p-2">{item.title || "—"}</td>
                                        <td className="p-2">{item.status ? <StatusPill v={String(item.status)} /> : "—"}</td>
                                        <td className="p-2">{item.next_due_date || "—"}</td>
                                        <td className="p-2">{item.actor || "—"}</td>
                                        {isOwner ? <td className="p-2">{item.cost != null ? formatCurrency(item.cost, cur) : "—"}</td> : null}
                                        <td className="p-2">
                                            <Button size="sm" variant="outline" onClick={() => setSelected(item)}>{i18n.t("batch2.69bd4ef9fbd0")}</Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <SimpleTable
                title={i18n.t("batch2.b8273ffd22de")}
                empty="No upcoming schedules."
                rows={scheduleUpcoming}
                columns={[
                    { header: "Date", key: "scheduled_date", render: (v) => v || "—" },
                    { header: "Type", key: "schedule_type", render: (v) => v || "—" },
                    { header: "Name", key: "schedule_name", render: (v) => v || "—" },
                    { header: "Dose", key: "dose", render: (v) => v || "—" },
                    { header: "Route", key: "route", render: (v) => v || "—" },
                    { header: "Priority", key: "priority", render: (v) => v || "medium" },
                    { header: "Assigned", key: "assigned_employee_name", render: (_, row) => row.assigned_employee_name || row.assigned_employee_id || "—" },
                    { header: "Status", key: "status", render: (v) => <StatusPill v={v} /> },
                ]}
                testid="animal-medical-schedule-upcoming-table"
            />

            <SimpleTable
                title={i18n.t("batch2.dab547ca0781")}
                empty="No overdue schedules."
                rows={scheduleOverdue}
                columns={[
                    { header: "Date", key: "scheduled_date", render: (v) => v || "—" },
                    { header: "Type", key: "schedule_type", render: (v) => v || "—" },
                    { header: "Name", key: "schedule_name", render: (v) => v || "—" },
                    { header: "Assigned", key: "assigned_employee_name", render: (_, row) => row.assigned_employee_name || row.assigned_employee_id || "—" },
                    { header: "Priority", key: "priority", render: (v) => v || "medium" },
                    { header: "Status", key: "status", render: (v) => <StatusPill v={v} /> },
                ]}
                testid="animal-medical-schedule-overdue-table"
            />

            <SimpleTable
                title={i18n.t("batch2.3f82cd24f99b")}
                empty="No completed schedules."
                rows={scheduleCompleted}
                columns={[
                    { header: "Completed", key: "completed_date", render: (v) => v || "—" },
                    { header: "Type", key: "schedule_type", render: (v) => v || "—" },
                    { header: "Name", key: "schedule_name", render: (v) => v || "—" },
                    { header: "Record Link", key: "completed_collection", render: (_, row) => row.completed_collection ? `${row.completed_collection}:${row.completed_record_id || "—"}` : "—" },
                    { header: "By", key: "completed_by", render: (v) => v || "—" },
                    { header: "Status", key: "status", render: (v) => <StatusPill v={v} /> },
                ]}
                testid="animal-medical-schedule-completed-table"
            />

            <SimpleTable
                title={i18n.t("batch2.2e71a937eb6d")}
                empty="No health inspections recorded."
                rows={inspections}
                columns={[
                    { header: "Date", key: "date" },
                    { header: "Issue/Condition", key: "issue", render: (_, row) => row.issue || row.condition || row.description || "—" },
                    { header: "Status", key: "status", render: (v) => <StatusPill v={v} /> },
                    { header: "Notes", key: "notes" },
                ]}
                testid="animal-health-table"
            />
            <SimpleTable
                title={i18n.t("batch2.36da837e8b37")}
                empty="No medication records."
                rows={treatments}
                columns={[
                    { header: "Date", key: "date" },
                    { header: "Type", key: "treatment_type", render: (v) => v ? String(v).replace(/_/g, " ") : "medication" },
                    { header: "Medicine", key: "medicine", render: (_, row) => row.medicine || row.medicine_name || "—" },
                    { header: "Issue", key: "issue", render: (v) => v || "—" },
                    { header: "Vet Notes", key: "vet_notes", render: (_, row) => row.vet_notes || row.notes || "—" },
                    { header: "Recovery", key: "recovery_status", render: (_, row) => row.recovery_status || row.status || "—" },
                    ...(isOwner ? [{ header: "Cost", key: "cost", render: (_, row) => {
                        const cost = resolveCost(row);
                        return cost != null ? formatCurrency(cost, cur) : "—";
                    } }] : []),
                ]}
                testid="animal-treatments-table"
            />
            <SimpleTable
                title={i18n.t("batch2.7b95b5178611")}
                empty="No vaccination records."
                rows={vaccinations}
                columns={[
                    { header: "Date", key: "scheduled_date", render: (_, row) => row.date_given || row.scheduled_date || "—" },
                    { header: "Vaccine", key: "vaccine", render: (_, row) => row.vaccine || row.vaccine_name || "—" },
                    { header: "Batch", key: "batch_number", render: (v) => v || "—" },
                    { header: "Dose", key: "dose" },
                    { header: "Next Due", key: "next_due_date", render: (v) => v || "—" },
                    { header: "Vet Notes", key: "vet_notes", render: (_, row) => row.vet_notes || row.notes || "—" },
                    { header: "Status", key: "status", render: (v) => <StatusPill v={v} /> },
                ]}
                testid="animal-vaccinations-table"
            />

            {selected ? (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
                    <Card className="w-full max-w-2xl p-4" onClick={(e) => e.stopPropagation()} data-testid="animal-health-record-modal">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold capitalize">{selected.type.replace(/_/g, " ")} {i18n.t("batch2.275a700078f0")}</h3>
                            <Button size="sm" variant="outline" onClick={() => setSelected(null)}>{i18n.t("batch2.bbfa773e5a63")}</Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            <Row k="Date" v={selected.date || "—"} />
                            <Row k="Status" v={selected.status || "—"} />
                            <Row k="Record" v={selected.title || "—"} />
                            <Row k="Recorded by" v={selected.actor || "—"} />
                            <Row k="Next due / follow-up" v={selected.next_due_date || "—"} />
                            {isOwner ? <Row k="Cost" v={selected.cost != null ? formatCurrency(selected.cost, cur) : "—"} /> : null}
                            <div className="md:col-span-2">
                                <Row k="Notes" v={selected.notes || "—"} />
                            </div>
                        </div>
                    </Card>
                </div>
            ) : null}
        </div>
    );
}

function BreedingTab({ p, onAdd, supportsMammalReproduction }) {
    const breedingRows = (Array.isArray(p.breedings) ? p.breedings : [])
        .slice()
        .sort((a, b) => `${b.service_date || b.date || ""}T${b.created_at || b.timestamp || ""}`.localeCompare(`${a.service_date || a.date || ""}T${a.created_at || a.timestamp || ""}`));
    const pregnancyRows = (Array.isArray(p.pregnancy_checks) ? p.pregnancy_checks : [])
        .slice()
        .sort((a, b) => `${b.date || ""}T${b.created_at || b.timestamp || ""}`.localeCompare(`${a.date || ""}T${a.created_at || a.timestamp || ""}`));

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 print:hidden">
                <Button size="sm" variant="outline" onClick={() => onAdd && onAdd("breeding")}>{i18n.t("batch2.9277a4e58ef5")}</Button>
                {supportsMammalReproduction ? <Button size="sm" variant="outline" onClick={() => onAdd && onAdd("pregnancy_check")}>{i18n.t("batch2.7794d275d6c6")}</Button> : null}
                {supportsMammalReproduction ? <Button size="sm" variant="outline" onClick={() => onAdd && onAdd("farrowing")}>{i18n.t("batch2.ed8cb2617ecd")}</Button> : null}
            </div>
            <SimpleTable
                title={i18n.t("batch2.f93f25f348f3")}
                empty="No breeding records."
                rows={breedingRows}
                columns={[
                    { header: "Breeding Date", key: "service_date", render: (_, row) => row.service_date || row.breeding_date || row.date || "—" },
                    { header: "Heat Date", key: "heat_date", render: (v) => v || "—" },
                    { header: "Female", key: "female_tag", render: (_, row) => row.female_tag || row.dam_tag || row.dam_id || "—" },
                    { header: "Male", key: "male_tag", render: (_, row) => row.male_tag || row.sire_tag || row.sire_id || "—" },
                    { header: "Method", key: "method", render: (v) => v ? String(v).toUpperCase() : "—" },
                    { header: "Technician", key: "technician", render: (v, row) => v || row.technician_name || row.employee_name || "—" },
                    { header: "Pregnancy", key: "pregnancy_status", render: (v) => v || "pending" },
                    { header: "Expected Farrowing", key: "expected_farrowing_date", render: (v) => v || "—" },
                    { header: "Due (days)", key: "due_countdown_days", render: (v) => (v === 0 || v ? v : "—") },
                    { header: "Rebreed", key: "rebreed_due_date", render: (v) => v || "—" },
                    { header: "Notes", key: "notes" },
                    { header: "Photo", key: "photo_url", render: (v, row) => (v || row.photo_data_url) ? "Yes" : "—" },
                ]}
                testid="animal-breeding-table"
            />
            <SimpleTable
                title={i18n.t("batch2.515dd86c51f4")}
                empty="No pregnancy checks."
                rows={pregnancyRows}
                columns={[
                    { header: "Date", key: "date" },
                    { header: "Status", key: "pregnancy_status", render: (v, row) => v || row.result || "—" },
                    { header: "Due Date", key: "estimated_due_date" },
                    { header: "Due (days)", key: "due_countdown_days", render: (v) => (v === 0 || v ? v : "—") },
                    { header: "Notes", key: "notes" },
                ]}
                testid="animal-pregnancy-table"
            />
            <SimpleTable
                title={i18n.t("batch2.5b4a40f54485")}
                empty="No farrowing records."
                rows={Array.isArray(p.farrowings) ? p.farrowings : []}
                columns={[
                    { header: "Date", key: "date", render: (_, row) => row.date || row.farrowing_date || "—" },
                    { header: "Litter", key: "litter_size" },
                    { header: "Live", key: "live_born" },
                    { header: "Stillborn", key: "stillborn" },
                    { header: "Mummified", key: "mummified", render: (v) => v ?? "—" },
                    { header: "Weak", key: "weak_piglets", render: (v) => v ?? "—" },
                    { header: "Birth Wt", key: "birth_weight", render: (v) => (v === 0 || v ? `${v} kg` : "—") },
                    { header: "Complications", key: "complications", render: (v) => v || "—" },
                    { header: "Assist", key: "assistance_required", render: (v) => v || "—" },
                    { header: "Piglets Created", key: "piglets_created", render: (v) => v ?? "—" },
                    { header: "Notes", key: "notes" },
                ]}
                testid="animal-farrowing-table"
            />
        </div>
    );
}

function GestationTab({ p, onAdd, supportsMammalReproduction }) {
    const rows = (Array.isArray(p.gestations) ? p.gestations : [])
        .slice()
        .sort((a, b) => `${b.expected_farrowing_date || ""}T${b.created_at || ""}`.localeCompare(`${a.expected_farrowing_date || ""}T${a.created_at || ""}`));

    return (
        <div className="space-y-4">
            {supportsMammalReproduction ? <div className="flex flex-wrap gap-2 print:hidden">
                <Button size="sm" variant="outline" onClick={() => onAdd && onAdd("gestation")}>{i18n.t("batch2.5c5ad63cd148")}</Button>
                <Button size="sm" variant="outline" onClick={() => onAdd && onAdd("pregnancy_check")}>{i18n.t("batch2.7794d275d6c6")}</Button>
            </div> : null}
            <SimpleTable
                title={i18n.t("batch2.5cbf60902ee2")}
                empty="No gestation records."
                rows={rows}
                columns={[
                    { header: "Breeding Date", key: "breeding_date", render: (v) => v || "—" },
                    { header: "Expected Farrowing", key: "expected_farrowing_date", render: (v) => v || "—" },
                    { header: "Current Stage", key: "pregnancy_stage", render: (v) => v || "—" },
                    { header: "Remaining Days", key: "remaining_days", render: (v) => (v === 0 || v ? v : "—") },
                    { header: "Pregnancy Status", key: "pregnancy_status", render: (v) => v || "—" },
                    { header: "Gestation Status", key: "gestation_status", render: (v) => v || "—" },
                    { header: "Notes", key: "notes", render: (v) => v || "—" },
                ]}
                testid="animal-gestation-table"
            />
        </div>
    );
}

function FeedingTab({ p, onAdd }) {
    const rows = (Array.isArray(p.feedings) ? p.feedings : [])
        .slice()
        .sort((a, b) => `${b.date || ""}T${b.created_at || b.timestamp || ""}`.localeCompare(`${a.date || ""}T${a.created_at || a.timestamp || ""}`));

    const resolveFeedCost = (row) => {
        const candidates = [row?.total_cost, row?.cost, row?.amount];
        for (const value of candidates) {
            if (value === 0 || value === "0") return 0;
            if (value !== null && value !== undefined && value !== "") {
                const n = Number(value);
                if (Number.isFinite(n)) return n;
            }
        }
        const qty = Number(row?.feed_kg ?? row?.quantity ?? 0);
        const cpu = Number(row?.cost_per_unit ?? 0);
        if (Number.isFinite(qty) && Number.isFinite(cpu) && qty > 0 && cpu > 0) {
            return qty * cpu;
        }
        return null;
    };

    return (
        <div className="space-y-3">
        <TabHeader title="Feeding History" onAdd={() => onAdd && onAdd("feeding")} testid="add-feeding-btn" />
        <SimpleTable
            title={i18n.t("batch2.4ec763aab4f8")}
            empty="No feeding records."
            rows={rows}
            columns={[
                { header: "Date/Time", key: "date", render: (v, r) => `${v || "—"}${r.timestamp ? ` ${String(r.timestamp).slice(11, 16)}` : ""}` },
                { header: "Feed Type", key: "feed_type" },
                { header: "Quantity", key: "feed_kg", render: (v, r) => {
                    const qty = v ?? r.quantity;
                    const unit = r.feed_unit || r.unit || "kg";
                    return qty !== null && qty !== undefined && qty !== "" ? `${qty} ${unit}` : "—";
                } },
                { header: "By", key: "employee_name", render: (v) => v || "—" },
                { header: "Notes", key: "notes" },
                { header: "Cost", key: "total_cost", render: (_v, r) => {
                    const cost = resolveFeedCost(r);
                    return cost === null ? "—" : `KES ${Number(cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                } },
            ]}
            testid="animal-feeding-table"
        />
        </div>
    );
}

function DailyCareTab({ p, onAdd }) {
    const water = Array.isArray(p.water_checks) ? p.water_checks : [];
    const movements = Array.isArray(p.movements) ? p.movements : [];
    const observations = (Array.isArray(p.health) ? p.health : []).filter((row) => String(row.type || "").toLowerCase() === "observation");
    const mortality = p.mortality ? [p.mortality] : [];
    return (
        <div className="space-y-4" data-testid="animal-daily-care">
            <div className="flex flex-wrap gap-2 print:hidden">
                <Button size="sm" variant="outline" onClick={() => onAdd("water")} data-testid="add-water-btn"><Droplets className="mr-1 h-4 w-4" />Water Check</Button>
                <Button size="sm" variant="outline" onClick={() => onAdd("movement")} data-testid="add-movement-btn"><MapPin className="mr-1 h-4 w-4" />Movement / Housing</Button>
                <Button size="sm" variant="outline" onClick={() => onAdd("observation")} data-testid="add-observation-btn"><NotebookPen className="mr-1 h-4 w-4" />Note / Observation</Button>
                {!p.mortality ? <Button size="sm" variant="destructive" onClick={() => onAdd("mortality")} data-testid="add-mortality-btn"><Skull className="mr-1 h-4 w-4" />Mortality</Button> : null}
            </div>
            <SimpleTable title="Water History" empty="No water checks recorded." rows={water} columns={[
                { header: "Date", key: "date" }, { header: "Location", key: "location" }, { header: "Quality", key: "water_quality", render: (v) => v ? String(v).replace(/_/g, " ") : "—" }, { header: "Amount", key: "amount", render: (v, row) => v !== null && v !== undefined ? `${v} ${row.unit || "L"}` : "—" }, { header: "Notes", key: "notes" },
            ]} testid="animal-water-table" />
            <SimpleTable title="Movement / Housing History" empty="No movements recorded." rows={movements} columns={[
                { header: "Date", key: "date" }, { header: "From", key: "from_location" }, { header: "To", key: "to_location" }, { header: "Reason", key: "reason" }, { header: "Notes", key: "notes" },
            ]} testid="animal-movement-table" />
            <SimpleTable title="Notes / Observations" empty="No observations recorded." rows={observations} columns={[
                { header: "Date", key: "date" }, { header: "Observation", key: "issue", render: (v, row) => v || row.description || "—" }, { header: "Notes", key: "notes" },
            ]} testid="animal-observation-table" />
            <SimpleTable title="Mortality" empty="No mortality record." rows={mortality} columns={[
                { header: "Date", key: "date" }, { header: "Cause", key: "cause" }, { header: "Disposition", key: "disposal_method" }, { header: "Notes", key: "notes" },
            ]} testid="animal-mortality-table" />
        </div>
    );
}

function DocumentsTab({ p, aid, onReload }) {
    const attachments = p.attachments || {};
    const photos = Array.isArray(attachments.photos) ? attachments.photos : [];
    const documents = Array.isArray(attachments.documents) ? attachments.documents : (Array.isArray(p.documents) ? p.documents : []);
    const receipts = Array.isArray(attachments.receipts) ? attachments.receipts : [];
    const [photoFiles, setPhotoFiles] = useState([]);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [docFiles, setDocFiles] = useState([]);
    const [docType, setDocType] = useState("attachment");
    const [docNotes, setDocNotes] = useState("");
    const [docUploading, setDocUploading] = useState(false);

    const photoPreviews = useMemo(
        () => photoFiles.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
        [photoFiles]
    );

    useEffect(() => {
        return () => {
            photoPreviews.forEach((item) => URL.revokeObjectURL(item.url));
        };
    }, [photoPreviews]);

    const uploadPhotos = async () => {
        if (!photoFiles.length) {
            toast.error("Select one or more photos first.");
            return;
        }
        setPhotoUploading(true);
        try {
            const fd = new FormData();
            photoFiles.forEach((f) => fd.append("files", f));
            fd.append("notes", "Uploaded from animal profile gallery");
            await api.post(`/animals/${aid}/photos`, fd);
            toast.success("Photos uploaded");
            setPhotoFiles([]);
            onReload && onReload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
        } finally {
            setPhotoUploading(false);
        }
    };

    const uploadDocuments = async () => {
        if (!docFiles.length) {
            toast.error("Select one or more files first.");
            return;
        }
        setDocUploading(true);
        try {
            const fd = new FormData();
            docFiles.forEach((f) => fd.append("files", f));
            fd.append("doc_type", docType || "attachment");
            fd.append("notes", docNotes || "");
            await api.post(`/animals/${aid}/documents`, fd);
            toast.success("Documents uploaded");
            setDocFiles([]);
            setDocNotes("");
            onReload && onReload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Document upload failed");
        } finally {
            setDocUploading(false);
        }
    };

    const deletePhoto = async (row) => {
        if (!row?.id) return;
        try {
            await api.delete(`/animals/${aid}/photos/${row.id}`);
            toast.success("Photo deleted");
            onReload && onReload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
        }
    };

    const deleteDocument = async (row) => {
        if (!row?.id) return;
        if (row.source !== "animal_documents") {
            toast.error("This legacy document cannot be deleted from this screen.");
            return;
        }
        try {
            await api.delete(`/animals/${aid}/documents/${row.id}`);
            toast.success("Document deleted");
            onReload && onReload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="space-y-4">
            <Card className="p-4" data-testid="animal-photo-gallery">
                <h3 className="font-semibold mb-3">{i18n.t("batch2.44ef3f997add")}</h3>
                <div className="grid gap-2 md:grid-cols-[1fr_auto] mb-3">
                    <Input type="file" accept="image/*" multiple onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))} />
                    <Button onClick={uploadPhotos} disabled={photoUploading}>{photoUploading ? "Uploading..." : "Upload Photos"}</Button>
                </div>
                {photoPreviews.length > 0 && (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                        {photoPreviews.map((item, idx) => (
                            <div key={`${item.name}-${idx}`} className="rounded border overflow-hidden">
                                <img src={item.url} alt={item.name} className="h-20 w-full object-cover" />
                            </div>
                        ))}
                    </div>
                )}
                {photos.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch2.7cc6e4582b56")}</div>}
                {photos.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {photos.map((photo, idx) => {
                            const src = photo.url || photo.photo_url || photo.image_url;
                            return (
                                <a
                                    key={`${photo.id || "photo"}-${idx}`}
                                    href={src || "#"}
                                    target={src ? "_blank" : undefined}
                                    rel="noreferrer"
                                    className="block rounded-lg border overflow-hidden bg-muted/20"
                                >
                                    {src ? (
                                        <img src={src} alt={photo.name || `Photo ${idx + 1}`} className="w-full h-32 object-cover" />
                                    ) : (
                                        <div className="h-32 grid place-items-center text-xs text-muted-foreground">{i18n.t("batch2.e49000cd16ad")}</div>
                                    )}
                                    <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{photo.name || `Photo ${idx + 1}`}</div>
                                    {photo.source === "animal_photos" && (
                                        <div className="px-2 pb-2">
                                            <Button type="button" variant="outline" size="sm" className="w-full" onClick={(e) => { e.preventDefault(); deletePhoto(photo); }}>
                                                {i18n.t("batch2.f6fdbe48dc54")}
                                            </Button>
                                        </div>
                                    )}
                                </a>
                            );
                        })}
                    </div>
                )}
            </Card>

            <Card className="p-4">
                <h3 className="font-semibold mb-3">{i18n.t("batch2.be2218931154")}</h3>
                <div className="grid gap-2 md:grid-cols-2">
                    <select value={docType} onChange={(e) => setDocType(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                        <option value="certificate">{i18n.t("batch2.10de46b3b341")}</option>
                        <option value="receipt">{i18n.t("batch2.f6ce3b6fcfca")}</option>
                        <option value="report">{i18n.t("batch2.ee45c30326b7")}</option>
                        <option value="attachment">{i18n.t("batch2.83a56249e5cf")}</option>
                        <option value="other">{i18n.t("batch2.6e6a6f2086bb")}</option>
                    </select>
                    <Input type="file" multiple onChange={(e) => setDocFiles(Array.from(e.target.files || []))} />
                </div>
                <Input className="mt-2" placeholder={i18n.t("batch2.4d56ca9be0c8")} value={docNotes} onChange={(e) => setDocNotes(e.target.value)} />
                <Button className="mt-2" onClick={uploadDocuments} disabled={docUploading}>{docUploading ? "Uploading..." : "Upload Documents"}</Button>
            </Card>

            <SimpleTable
                title={i18n.t("batch2.687c82861c95")}
                empty="No documents linked."
                rows={documents}
                columns={[
                    { header: "Date", key: "created_at" },
                    { header: "Name", key: "name", render: (_, row) => row.name || row.filename || row.original_filename || "—" },
                    { header: "Type", key: "type", render: (_, row) => row.type || row.mime_type || "—" },
                    { header: "Notes", key: "notes", render: (v) => v || "—" },
                    { header: "Action", key: "id", render: (_, row) => <Button type="button" variant="outline" size="sm" onClick={() => deleteDocument(row)}>{i18n.t("batch2.f6fdbe48dc54")}</Button> },
                ]}
                testid="animal-documents-table"
            />

            <SimpleTable
                title={i18n.t("batch2.d6dadec06672")}
                empty="No receipts linked."
                rows={receipts}
                columns={[
                    { header: "Date", key: "created_at" },
                    { header: "Name", key: "name", render: (_, row) => row.name || row.original_filename || "—" },
                    { header: "Type", key: "type", render: (_, row) => row.type || "receipt" },
                    { header: "Notes", key: "notes", render: (v) => v || "—" },
                    { header: "Action", key: "id", render: (_, row) => row.source === "animal_documents" ? <Button type="button" variant="outline" size="sm" onClick={() => deleteDocument(row)}>{i18n.t("batch2.f6fdbe48dc54")}</Button> : "—" },
                ]}
                testid="animal-receipts-table"
            />
        </div>
    );
}

function BirthTab({ p }) {
    const br = p.birth_record;
    if (!br) return <Card className="p-6 text-sm text-muted-foreground">{i18n.t("batch2.9978c7eb8d75")}</Card>;
    return (
        <Card className="p-4 space-y-2 text-sm" data-testid="animal-birth-card">
            <Row k="Birth Record" v={br.number || br.id} />
            <Row k="Date" v={br.birth_date} />
            <Row k="Time" v={br.birth_time} />
            <Row k="Location" v={br.location} />
            <Row k="Litter #" v={br.litter_number} />
            <Row k="Born Alive" v={br.born_alive} />
            <Row k="Stillborn" v={br.stillborn} />
            <Row k="Weak at Birth" v={br.weak_at_birth} />
            <Row k="Birth Weight" v={br.birth_weight} />
            <Row k="Condition" v={br.birth_condition} />
            <Row k="Assisted" v={br.assisted ? "Yes" : "No"} />
            <Row k="Attending Staff" v={br.staff} />
            <Row k="Notes" v={br.notes} />
        </Card>
    );
}

function FamilyTab({ p }) {
    const { mother, father, siblings, offspring } = p.family;
    const [grand, setGrand] = useState({ mm: null, mf: null, fm: null, ff: null });

    useEffect(() => {
        // Fetch one level up (grandparents) for a mini pedigree tree
        const fetchGP = async () => {
            const jobs = [];
            const src = { mm: mother?.mother_id, mf: mother?.father_id, fm: father?.mother_id, ff: father?.father_id };
            for (const [k, id] of Object.entries(src)) {
                if (id) jobs.push(api.get(`/animals/${id}`).then((r) => [k, r.data]).catch(() => [k, null]));
            }
            const results = await Promise.all(jobs);
            const next = { mm: null, mf: null, fm: null, ff: null };
            results.forEach(([k, v]) => { next[k] = v; });
            setGrand(next);
        };
        if (mother || father) fetchGP();
    }, [mother, father]);

    return (
        <div className="space-y-4" data-testid="animal-family-tab">
            {/* Pedigree tree (3 generations) */}
            <Card className="p-4">
                <div className="tiny-label mb-3">{i18n.t("batch2.01e479028989")}</div>
                <div className="overflow-x-auto">
                    <div className="min-w-max flex items-center gap-4">
                        {/* Grandparents column */}
                        <div className="flex flex-col gap-1.5">
                            <PedigreeSlot a={grand.mm} label={i18n.t("batch2.394f35e11401")} testid="pedigree-mm" />
                            <PedigreeSlot a={grand.mf} label={i18n.t("batch2.40e64b44039a")} testid="pedigree-mf" />
                            <PedigreeSlot a={grand.fm} label={i18n.t("batch2.62096e1d4d84")} testid="pedigree-fm" />
                            <PedigreeSlot a={grand.ff} label={i18n.t("batch2.f97872e1cdbb")} testid="pedigree-ff" />
                        </div>
                        <div className="text-muted-foreground">→</div>
                        {/* Parents column */}
                        <div className="flex flex-col gap-1.5">
                            <PedigreeSlot a={mother} label={i18n.t("batch2.a7502c1c8415")} testid="pedigree-mother" />
                            <PedigreeSlot a={father} label={i18n.t("batch2.2a6c6b93fb28")} testid="pedigree-father" />
                        </div>
                        <div className="text-muted-foreground">→</div>
                        {/* This animal */}
                        <div className="border-2 border-primary rounded-lg p-3 bg-primary/10 min-w-[140px] text-center">
                            <div className="tiny-label mb-1 text-primary">{i18n.t("batch2.8f210c7b2cfe")}</div>
                            <div className="font-bold">{p.animal.tag_id || p.animal.name}</div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <div className="tiny-label mb-2">{i18n.t("batch2.079bd7775895")}{siblings.length})</div>
                {siblings.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch2.6eef6648406c")}</div>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {siblings.map((s) => <AnimalLink key={s.id} a={s} />)}
                </div>
            </Card>
            <Card className="p-4">
                <div className="tiny-label mb-2">{i18n.t("batch2.61b5b59fbab8")}{offspring.length})</div>
                {offspring.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch2.6eef6648406c")}</div>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {offspring.map((o) => <AnimalLink key={o.id} a={o} />)}
                </div>
            </Card>
        </div>
    );
}

function AnimalLink({ a }) {
    return (
        <Link to={`/animals/${a.id}`} className="block rounded-lg border p-2 text-sm hover:bg-muted transition" data-testid={`family-link-${a.id}`}>
            <div className="font-semibold truncate">{a.tag_id || a.name || "—"}</div>
            <div className="text-xs text-muted-foreground truncate">{a.breed || ""}</div>
        </Link>
    );
}

function PedigreeSlot({ a, label, testid }) {
    return (
        <div className="border rounded-lg p-2 text-center min-w-[120px]" data-testid={testid}>
            <div className="tiny-label mb-1">{label}</div>
            {a ? (
                <Link to={`/animals/${a.id}`} className="text-sm font-semibold hover:text-primary">
                    {a.tag_id || a.name || "—"}
                </Link>
            ) : <div className="text-xs text-muted-foreground">—</div>}
        </div>
    );
}

function WeightTab({ p, latestWeightValue }) {
    const chartWrapRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(0);

    useEffect(() => {
        const el = chartWrapRef.current;
        if (!el) return undefined;
        const update = () => {
            const next = Math.max(0, Math.floor(el.getBoundingClientRect().width || 0));
            setChartWidth(next);
        };
        update();
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(update);
            ro.observe(el);
            return () => ro.disconnect();
        }
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, []);

    const rows = (Array.isArray(p.weights) ? p.weights : []).map((w) => ({
        id: w.id,
        date: w.date,
        created_at: w.created_at,
        weight: Number(w.weight || w.weight_kg || w.value || 0),
        feed_kg: Number(w.feed_kg || 0),
        actor: w.employee_name || w.recorded_by || w.created_by || w.user_name || "—",
        target: Number((p.species && p.species.target_weight) || 0),
        notes: w.notes || "",
    })).filter((row) => Number.isFinite(row.weight) && row.weight > 0).sort((a, b) => `${a.date || ""}T${a.created_at || ""}`.localeCompare(`${b.date || ""}T${b.created_at || ""}`));

    const monthlyGainMap = useMemo(() => {
        const byMonth = {};
        rows.forEach((row) => {
            const month = (row.date || "").slice(0, 7) || "unknown";
            byMonth[month] = byMonth[month] || [];
            byMonth[month].push(row);
        });
        const gains = {};
        Object.entries(byMonth).forEach(([month, list]) => {
            if (!list.length) {
                gains[month] = null;
                return;
            }
            const first = list[0].weight;
            const last = list[list.length - 1].weight;
            gains[month] = Number((last - first).toFixed(2));
        });
        return gains;
    }, [rows]);

    const rowsWithGain = rows.map((row, idx) => {
        if (idx === 0) {
            return { ...row, previous_weight: null, gain: null, days: null, daily_gain: null, monthly_gain: monthlyGainMap[(row.date || "").slice(0, 7)] };
        }
        const prev = rows[idx - 1];
        const gain = row.weight - prev.weight;
        const prevDate = new Date(prev.date || "");
        const currDate = new Date(row.date || "");
        const days = Number.isFinite(prevDate.getTime()) && Number.isFinite(currDate.getTime())
            ? Math.max(1, Math.round((currDate.getTime() - prevDate.getTime()) / 86400000))
            : 1;
        return {
            ...row,
            previous_weight: prev.weight,
            gain: Number(gain.toFixed(2)),
            days,
            daily_gain: Number((gain / days).toFixed(3)),
            monthly_gain: monthlyGainMap[(row.date || "").slice(0, 7)],
        };
    });

    const exportCsv = () => {
        if (!rowsWithGain.length) {
            toast.error("No weight history to export.");
            return;
        }
        const header = ["date", "weight_kg", "feed_kg", "gain_kg", "days_since_prev", "daily_gain_kg", "monthly_gain_kg", "notes"];
        const lines = rowsWithGain.map((row) => [
            row.date || "",
            row.weight ?? "",
            row.feed_kg ?? "",
            row.gain ?? "",
            row.days ?? "",
            row.daily_gain ?? "",
            row.monthly_gain ?? "",
            (row.notes || "").replace(/\n/g, " "),
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
        const csv = [header.join(","), ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${p.animal?.tag_id || p.animal?.id || "animal"}-weight-history.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const latestWeightDisplay = latestWeightValue != null ? `${latestWeightValue.toFixed(1)} kg` : "—";
    const totalGainDisplay = p.metrics?.total_gain != null ? `${Number(p.metrics.total_gain).toFixed(1)} kg` : "—";
    const adgDisplay = p.metrics?.avg_daily_gain != null ? `${Number(p.metrics.avg_daily_gain).toFixed(2)} kg/d` : "—";

    return (
        <Card className="p-4" data-testid="animal-weight-chart">
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">{i18n.t("batch2.e3c2f30bf6f0")}</h3>
                <Button size="sm" variant="outline" onClick={exportCsv}>{i18n.t("batch2.5755f9ac0aa1")}</Button>
            </div>
            {rows.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">{i18n.t("batch2.eebf5dd44b08")}</div>}
            {rows.length > 0 && (
                <div ref={chartWrapRef} style={{ height: 260 }}>
                    {chartWidth > 0 ? (
                        <LineChart width={chartWidth} height={260} data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="weight" stroke="#40916C" strokeWidth={2.5} dot />
                            {rows[0].target > 0 && <Line type="monotone" dataKey="target" stroke="#F4A261" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
                        </LineChart>
                    ) : null}
                </div>
            )}
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <div className="border rounded-lg p-2"><div className="tiny-label">{i18n.t("batch2.decd7ca80010")}</div><div className="font-bold">{latestWeightDisplay}</div></div>
                <div className="border rounded-lg p-2"><div className="tiny-label">{i18n.t("batch2.5e5837db8186")}</div><div className="font-bold">{totalGainDisplay}</div></div>
                <div className="border rounded-lg p-2"><div className="tiny-label">{i18n.t("batch2.6527e706017d")}</div><div className="font-bold">{adgDisplay}</div></div>
            </div>
            {rowsWithGain.length > 0 && (
                <div className="overflow-x-auto mt-4">
                    <table className="w-full text-sm border rounded-lg" data-testid="animal-weight-history-table">
                        <thead className="bg-muted/40">
                            <tr>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.eb9a4bc1c0c1")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.395703d5f5b9")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.ef2c495072dd")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.37dcaa9ab12f")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.6149ff653a46")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.80e519ac864e")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.d43c8e6c0c7e")}</th>
                                <th className="p-2 text-left text-xs uppercase text-muted-foreground">{i18n.t("batch2.70440046a3dc")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rowsWithGain.map((row, idx) => (
                                <tr key={row.id || idx} className="border-t border-border">
                                    <td className="p-2">{row.date || "—"}</td>
                                    <td className="p-2">{row.previous_weight == null ? "—" : `${row.previous_weight.toFixed(1)} kg`}</td>
                                    <td className="p-2">{row.weight.toFixed(1)} {i18n.t("batch2.1389845b02c0")}</td>
                                    <td className="p-2">{row.gain == null ? "—" : `${row.gain >= 0 ? "+" : ""}${row.gain.toFixed(2)} kg`}</td>
                                    <td className="p-2">{row.daily_gain == null ? "—" : `${row.daily_gain.toFixed(3)} kg/d`}</td>
                                    <td className="p-2">{row.monthly_gain == null ? "—" : `${row.monthly_gain >= 0 ? "+" : ""}${row.monthly_gain.toFixed(2)} kg`}</td>
                                    <td className="p-2">{row.actor || "—"}</td>
                                    <td className="p-2">{row.notes || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

function ExpensesTab({ p, cur }) {
    const rows = [
        { label: "Feed", key: "feed" },
        { label: "Vaccination", key: "vaccination" },
        { label: "Treatment / Medicine", key: "treatment" },
        { label: "Other", key: "other" },
    ];
    return (
        <Card className="p-4" data-testid="animal-expenses-card">
            <h3 className="font-semibold mb-3">{i18n.t("batch2.915586ea1401")}</h3>
            <div className="space-y-1.5">
                {rows.map((r) => (
                    <div key={r.key} className="flex items-center justify-between text-sm border-b border-border pb-1.5 last:border-0">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="font-mono font-semibold">{formatCurrency(p.expenses[r.key] || 0, cur)}</span>
                    </div>
                ))}
                <div className="flex items-center justify-between text-sm pt-2 mt-1 border-t-2 border-primary/30">
                    <span className="font-bold">{i18n.t("batch2.c26b31168973")}</span>
                    <span className="font-mono font-bold text-primary" data-testid="animal-expenses-total">{formatCurrency(p.expenses.total, cur)}</span>
                </div>
            </div>
        </Card>
    );
}

function SaleTab({ p, cur, onApprove, busy }) {
    const inv = p.sale_invoice;
    return (
        <div className="space-y-4">
            <Card className="p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />{i18n.t("batch2.ce31372bab62")}
                </h3>
                <div className={`text-lg font-bold mb-2 ${p.readiness.state === "ready" ? "text-primary" : p.readiness.state === "almost_ready" ? "text-amber-700" : "text-destructive"}`}>
                    {p.readiness.summary}
                </div>
                <Progress value={(p.readiness.passed / p.readiness.total) * 100} className="h-2 mb-3" />
                {p.readiness.blockers.length > 0 && (
                    <div className="rounded-lg bg-destructive/5 border border-destructive/30 p-3 text-sm">
                        <div className="flex items-center gap-2 font-semibold text-destructive mb-1">
                            <AlertTriangle className="w-4 h-4" />{i18n.t("batch2.699edf83caae")}
                        </div>
                        <ul className="list-disc pl-5 space-y-0.5">
                            {p.readiness.blockers.map((b) => (
                                <li key={b.key}><b>{b.label}</b> — {b.detail}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {!p.animal.sale_approved && (
                    <Button className="mt-3" onClick={onApprove} disabled={busy} data-testid="sale-tab-approve-btn">
                        {i18n.t("batch2.1615009c340b")}
                    </Button>
                )}
                {p.animal.sale_approved && (
                    <div className="mt-3 flex items-center gap-3">
                        <div className="text-xs text-muted-foreground flex-1">
                            {i18n.t("batch2.b953668f0a6d")} {p.animal.sale_approved_by} {i18n.t("batch2.db3d405b1067")} {p.animal.sale_approved_at?.slice(0, 10)}
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                                try {
                                    await api.put(`/animals/${p.animal.id}`, { ...p.animal, sale_approved: false, sale_approved_by: null, sale_approved_at: null });
                                    toast.success("Approval revoked");
                                    window.location.reload();
                                } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                            }}
                            data-testid="sale-tab-revoke-btn"
                        >
                            {i18n.t("batch2.0be720759ff0")}
                        </Button>
                    </div>
                )}
            </Card>
            {inv && (
                <Card className="p-4" data-testid="animal-sale-invoice-card">
                    <h3 className="font-semibold mb-2">{i18n.t("batch2.0a06508c0175")}</h3>
                    <Row k="Invoice #" v={inv.number || inv.id} />
                    <Row k="Date" v={inv.date || inv.created_at?.slice(0, 10)} />
                    <Row k="Buyer" v={inv.customer_name} />
                    <Row k="Total" v={formatCurrency(inv.total, cur)} />
                    <Row k="Status" v={inv.status} />
                </Card>
            )}
        </div>
    );
}

function TimelineTab({ items }) {
    const [search, setSearch] = useState("");
    const [eventType, setEventType] = useState("all");
    const [attachmentsOnly, setAttachmentsOnly] = useState(false);

    const events = Array.isArray(items) ? items : [];
    const eventTypes = useMemo(() => {
        const set = new Set(events.map((e) => e.eventType).filter(Boolean));
        return ["all", ...Array.from(set).sort()];
    }, [events]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return events.filter((e) => {
            if (eventType !== "all" && e.eventType !== eventType) return false;
            const hasMedia = Boolean((e.images || []).length || (e.attachments || []).length);
            if (attachmentsOnly && !hasMedia) return false;
            if (!needle) return true;
            const text = [
                e.eventType,
                e.details,
                e.notes,
                e.actor,
                ...(e.attachments || []).map((a) => a.name || ""),
            ].join(" ").toLowerCase();
            return text.includes(needle);
        });
    }, [events, search, eventType, attachmentsOnly]);

    if (events.length === 0) return <Card className="p-6 text-sm text-muted-foreground text-center">{i18n.t("batch2.257fe95db51c")}</Card>;
    return (
        <Card className="p-4" data-testid="animal-timeline">
            <div className="grid gap-2 md:grid-cols-[1fr_auto_auto] mb-4">
                <Input placeholder={i18n.t("batch2.4fa548ec107d")} value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {eventTypes.map((type) => (
                        <option key={type} value={type}>{type === "all" ? "All event types" : type.replace(/_/g, " ")}</option>
                    ))}
                </select>
                <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={attachmentsOnly} onChange={(e) => setAttachmentsOnly(e.target.checked)} />
                    {i18n.t("batch2.fcf1dded7a2a")}
                </label>
            </div>
            {filtered.length === 0 && <div className="text-sm text-muted-foreground">{i18n.t("batch2.780b2eb08d06")}</div>}
            {filtered.length > 0 && (
            <ol className="relative border-l-2 border-primary/30 ml-2 space-y-4">
                {filtered.map((e, i) => (
                    <li key={`${e.recordId || "na"}-${e.eventType || "event"}-${i}`} className="ml-4">
                        <div className="absolute -left-[9px] w-4 h-4 rounded-full bg-primary" />
                        <div className="text-xs text-muted-foreground font-mono">{e.eventDate || e.eventAt || "—"}{e.eventTime ? ` ${e.eventTime}` : ""}</div>
                        <div className="text-sm font-semibold capitalize">{(e.eventType || "event").replace(/_/g, " ")}</div>
                        <div className="text-sm">{e.details || "—"}</div>
                        {e.notes ? <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch2.9c3befe7e22c")} {e.notes}</div> : null}
                        <div className="text-xs text-muted-foreground">
                            {(e.actor ? `By ${e.actor}` : "By —")} · {e.recordId ? `Record ${e.recordId}` : "Record —"}{e.hasPhoto ? " · Photo" : ""}
                        </div>
                        {!!(e.images || []).length && (
                            <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-2">
                                {(e.images || []).slice(0, 6).map((img, idx) => (
                                    <a key={`${img}-${idx}`} href={img} target="_blank" rel="noreferrer" className="block rounded border overflow-hidden">
                                        <img src={img} alt={i18n.t("batch2.5006ed0248a0")} className="h-16 w-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        )}
                        {!!(e.attachments || []).length && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(e.attachments || []).map((att, idx) => (
                                    <a key={`${att.file_id || att.url || idx}-${idx}`} href={att.url || "#"} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded border bg-muted/30">
                                        {att.name || att.kind || "attachment"}
                                    </a>
                                ))}
                            </div>
                        )}
                    </li>
                ))}
            </ol>
            )}
        </Card>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex text-sm border-b border-border py-1 last:border-0">
            <span className="text-muted-foreground w-1/3">{k}</span>
            <span className="font-medium flex-1 truncate">{v || "—"}</span>
        </div>
    );
}

function StatusPill({ v }) {
    if (!v) return <span className="text-muted-foreground text-xs">—</span>;
    const c = ({
        completed: "bg-primary/15 text-primary",
        overdue: "bg-destructive/15 text-destructive",
        upcoming: "bg-blue-500/15 text-blue-700",
        due_today: "bg-amber-500/15 text-amber-700",
        under_treatment: "bg-amber-500/15 text-amber-700",
        under_observation: "bg-blue-500/15 text-blue-700",
        recovered: "bg-primary/15 text-primary",
        critical: "bg-destructive/15 text-destructive",
    })[v] || "bg-secondary text-foreground";
    return <span className={`px-2 py-0.5 rounded-md text-xs capitalize ${c}`}>{v.replace(/_/g, " ")}</span>;
}

function SimpleTable({ title, empty, rows, columns, testid }) {
    return (
        <Card className="p-4 overflow-hidden" data-testid={testid}>
            <h3 className="font-semibold mb-3">{title}</h3>
            {rows.length === 0 && <div className="text-sm text-muted-foreground py-4">{empty}</div>}
            {rows.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                            <tr>{columns.map((c) => <th key={c.key} className="p-2 text-left text-xs uppercase text-muted-foreground">{c.header}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.id || i} className="border-t border-border">
                                    {columns.map((c) => <td key={c.key} className="p-2">{c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}
